import { useOrderDetailController } from "./OrderDetailDialog.controller";
import {
  OrderDetailDialogPresentation,
  type OrderDetailDialogPresentationProps,
} from "./OrderDetailDialog.pres";
import { OrderDetailDialogFillsTablePresentation } from "./OrderDetailDialogFillsTable.pres";
import { OrderDetailDialogHeaderPresentation } from "./OrderDetailDialogHeader.pres";
import { OrderDetailDialogReplacementHistoryPresentation } from "./OrderDetailDialogReplacementHistory.pres";
import { OrderDetailDialogSummaryPresentation } from "./OrderDetailDialogSummary.pres";

export interface OrderDetailDialogProps {
  open: boolean;
  accountID?: string;
  product?: "live" | "paper";
  orderID?: string;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: OrderDetailDialogPresentationProps["onCloseAutoFocus"];
}

export const OrderDetailDialog = ({
  open,
  accountID,
  product,
  orderID,
  onOpenChange,
  onCloseAutoFocus,
}: OrderDetailDialogProps) => {
  const controller = useOrderDetailController({
    open,
    accountID,
    product,
    orderID,
    onOpenChange,
    onCloseAutoFocus,
  });

  const headerNode = (
    <OrderDetailDialogHeaderPresentation
      title={controller.title}
      description={controller.description}
      detail={controller.detail}
    />
  );
  const replacementHistoryNode = (
    <OrderDetailDialogReplacementHistoryPresentation
      history={controller.replacementHistory}
      isLoading={controller.isHistoryLoading}
      isFetching={controller.isHistoryFetching}
      onRetry={controller.onRetryHistory}
    />
  );
  const fillsTableNode = (
    <OrderDetailDialogFillsTablePresentation
      fills={controller.fills}
      isLoading={controller.isFillsLoading}
      isFetching={controller.isFillsFetching}
      isError={controller.isFillsError}
      errorMessage={controller.fillsErrorMessage}
      pageNumber={controller.fillsPageNumber}
      hasPreviousPage={controller.hasPreviousFillsPage}
      hasNextPage={controller.hasNextFillsPage}
      onRetry={controller.onRetryFills}
      onPreviousPage={controller.onPreviousFillsPage}
      onNextPage={controller.onNextFillsPage}
    />
  );
  const summaryNode = controller.detail ? (
    <OrderDetailDialogSummaryPresentation
      detail={controller.detail}
      replacementHistory={replacementHistoryNode}
      fillsTable={fillsTableNode}
    />
  ) : undefined;

  return (
    <OrderDetailDialogPresentation
      open={controller.open}
      header={headerNode}
      summary={summaryNode}
      hasDetail={Boolean(controller.detail)}
      contentLabel={
        controller.detail ? `${controller.title} content` : undefined
      }
      isLoading={controller.isLoading}
      isFetching={controller.isFetching}
      isError={controller.isError}
      isNotFound={controller.isNotFound}
      errorMessage={controller.errorMessage}
      onOpenChange={controller.onOpenChange}
      onCloseAutoFocus={controller.onCloseAutoFocus}
      onRetry={controller.onRetry}
    />
  );
};
